import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// =========================================================================
// /admin/content — Content-engine admin console (lean v1).
// Reads /api/content/v1 using the shared admin password header. Lets an
// editor:
//   * See the 25 drafted + any imported articles grouped by pillar/status
//   * Preview an article (title/pillar/body sample + canonical url)
//   * Publish a reviewed draft at /learn/<slug>
//   * Roll back to a prior revision
//   * Re-run the delivered seed batch
// Admin password is stored in sessionStorage (same convention as other
// admin pages) — never persisted to localStorage, never embedded in JS.
// =========================================================================

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/content/v1';

const usePassword = () => {
  const [pw, setPw] = useState(() => sessionStorage.getItem('fundle-admin-pw') || '');
  const save = (v) => {
    sessionStorage.setItem('fundle-admin-pw', v);
    setPw(v);
  };
  return [pw, save];
};

const headers = (pw) => ({ 'X-Admin-Password': pw, 'Content-Type': 'application/json' });

const PILLAR_LABELS = {
  loyalty: 'Loyalty', campaigns: 'Campaigns', marketplace: 'Marketplace',
  mbo: 'MBO', sis: 'SIS', ebo: 'EBO', 'social-commerce': 'Social commerce',
  malls: 'Malls', 'retail-ai': 'Governed AI',
};

const ContentEngineAdmin = () => {
  const [pw, savePw] = usePassword();
  const [pwField, setPwField] = useState(pw);
  const [articles, setArticles] = useState(null);
  const [filter, setFilter] = useState({ status: '', pillar: '' });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [sched, setSched] = useState(null);

  const loadSched = async () => {
    if (!pw) return;
    try {
      const r = await fetch(`${API_BASE}/scheduler/config`, { headers: headers(pw) });
      if (r.ok) setSched(await r.json());
    } catch { /* ignore */ }
  };

  const saveSched = async () => {
    if (!sched) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/scheduler/config`, {
        method: 'PUT',
        headers: headers(pw),
        body: JSON.stringify({ auto_publish: sched.auto_publish, max_publish_per_day: Number(sched.max_publish_per_day) }),
      });
      const j = await r.json();
      if (r.ok) { setToast(`Auto-publish ${j.auto_publish ? 'ON' : 'OFF'} · cap ${j.max_publish_per_day}/day`); await loadSched(); }
      else setToast(`Save failed: ${j.detail || 'error'}`);
    } finally { setBusy(false); }
  };

  const load = async () => {
    if (!pw) return;
    setBusy(true);
    try {
      const q = new URLSearchParams();
      if (filter.status) q.set('status', filter.status);
      if (filter.pillar) q.set('pillar', filter.pillar);
      q.set('limit', '200');
      const r = await fetch(`${API_BASE}/articles?${q}`, { headers: headers(pw) });
      if (r.status === 401) { setToast('Invalid password'); setArticles([]); return; }
      const j = await r.json();
      setArticles(j.items || []);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (pw) load(); // eslint-disable-next-line
  }, [pw, filter.status, filter.pillar]);

  useEffect(() => {
    if (pw) loadSched(); // eslint-disable-next-line
  }, [pw]);

  const grouped = useMemo(() => {
    const m = new Map();
    (articles || []).forEach((a) => {
      const k = a.pillar || 'other';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    });
    return m;
  }, [articles]);

  const counts = useMemo(() => {
    const c = { draft: 0, published: 0, hold: 0 };
    (articles || []).forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [articles]);

  const publish = async (a) => {
    if (!window.confirm(`Publish "${a.title}" to /learn/${a.slug}?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/articles/${a.stable_id}/publish`, {
        method: 'POST',
        headers: headers(pw),
        body: JSON.stringify({ expected_revision: a.revision }),
      });
      const j = await r.json();
      if (r.ok) {
        setToast(`Published ${a.slug} → ${j.canonical_url}`);
        await load();
        if (selected?.stable_id === a.stable_id) setSelected({ ...a, status: 'published', canonical_url: j.canonical_url });
      } else {
        setToast(`Publish failed: ${j.detail}`);
      }
    } finally { setBusy(false); }
  };

  const rollback = async (a) => {
    const target = window.prompt(`Rollback ${a.slug} to which prior revision? (currently ${a.revision})`);
    if (!target) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/articles/${a.stable_id}/rollback`, {
        method: 'POST',
        headers: headers(pw),
        body: JSON.stringify({ target_revision: parseInt(target, 10) }),
      });
      const j = await r.json();
      setToast(r.ok ? `Rolled back to new revision ${j.new_revision}` : `Rollback failed: ${j.detail}`);
      await load();
    } finally { setBusy(false); }
  };

  if (!pw) {
    return (
      <main data-testid="content-admin" style={{ padding: '48px 24px', maxWidth: 480, margin: '0 auto', fontFamily: 'Inter, system-ui' }}>
        <h1 style={{ fontSize: 28, color: '#0D1B2A' }}>Content Engine</h1>
        <p style={{ color: '#4b5a74' }}>Enter the shared admin password to continue.</p>
        <input
          data-testid="content-admin-pw"
          type="password"
          value={pwField}
          onChange={(e) => setPwField(e.target.value)}
          placeholder="Admin password"
          style={{ width: '100%', padding: 12, border: '1px solid #CBD5E1', borderRadius: 8, marginTop: 10 }}
        />
        <button
          data-testid="content-admin-pw-submit"
          onClick={() => savePw(pwField)}
          style={{ marginTop: 10, padding: '10px 20px', background: '#1E3A8A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
        >
          Enter
        </button>
      </main>
    );
  }

  return (
    <main data-testid="content-admin" style={{ padding: '32px 24px 96px', maxWidth: 1400, margin: '0 auto', fontFamily: 'Inter, system-ui', color: '#0D1B2A' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A', margin: 0 }}>
            Fundle Content Engine
          </p>
          <h1 style={{ fontSize: 28, margin: '6px 0 0', fontWeight: 800 }}>
            Articles · {articles?.length || 0}
            <span style={{ fontSize: 14, color: '#4b5a74', marginLeft: 14, fontWeight: 400 }}>
              Draft {counts.draft || 0} · Published {counts.published || 0} · Hold {counts.hold || 0}
            </span>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select value={filter.pillar} onChange={(e) => setFilter({ ...filter, pillar: e.target.value })}
                  style={{ padding: 8, border: '1px solid #CBD5E1', borderRadius: 8 }}>
            <option value="">All pillars</option>
            {Object.entries(PILLAR_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                  style={{ padding: 8, border: '1px solid #CBD5E1', borderRadius: 8 }}>
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="hold">Hold</option>
          </select>
          <a href={`${API_BASE}/openapi.json`} target="_blank" rel="noopener noreferrer"
             style={{ padding: '8px 14px', background: '#F8FBFF', color: '#1E3A8A', border: '1px solid #DCE4F0', borderRadius: 8, fontWeight: 700, textDecoration: 'none' }}>
            OpenAPI ↗
          </a>
        </div>
      </header>

      {toast && (
        <div data-testid="content-admin-toast" style={{ padding: '10px 14px', background: '#EFF6FF', color: '#1E3A8A', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          {toast}
        </div>
      )}

      {sched && (
        <div data-testid="sched-autopublish-panel" style={{
          padding: '16px 18px', border: '1px solid #E2E8F0', borderRadius: 12,
          background: sched.auto_publish ? '#F0FDF4' : '#F8FBFF', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A', margin: 0 }}>
              Hourly engine · auto-publish
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 13.5, color: '#4b5a74' }}>
              When ON, newly drafted articles publish straight to <strong>/learn</strong> up to the daily cap.
              {' '}Scheduler is <strong>{sched.scheduler_enabled ? 'running' : 'idle'}</strong> · published last 24h:{' '}
              <strong data-testid="sched-published-24h">{sched.published_last_24h}</strong>/{sched.max_publish_per_day}.
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            <input
              data-testid="sched-autopublish-toggle"
              type="checkbox"
              checked={!!sched.auto_publish}
              onChange={(e) => setSched({ ...sched, auto_publish: e.target.checked })}
              style={{ width: 18, height: 18 }}
            />
            Auto-publish {sched.auto_publish ? 'ON' : 'OFF'}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            Cap / day
            <input
              data-testid="sched-cap-input"
              type="number"
              min={0}
              max={50}
              value={sched.max_publish_per_day}
              onChange={(e) => setSched({ ...sched, max_publish_per_day: e.target.value })}
              style={{ width: 70, padding: 8, border: '1px solid #CBD5E1', borderRadius: 8 }}
            />
          </label>
          <button
            data-testid="sched-save"
            disabled={busy}
            onClick={saveSched}
            style={{ padding: '9px 18px', background: '#1E3A8A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
            Save
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 420px)', gap: 24 }}>
        <section>
          {articles === null && <p>Loading…</p>}
          {articles && [...grouped.keys()].sort().map((pil) => (
            <div key={pil} data-testid={`admin-pillar-${pil}`} style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 16, borderBottom: '1px solid #E2E8F0', paddingBottom: 8, color: '#0D1B2A' }}>
                {PILLAR_LABELS[pil] || pil}
                <span style={{ color: '#4b5a74', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>({grouped.get(pil).length})</span>
              </h2>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {grouped.get(pil).map((a) => (
                  <li key={a.stable_id}
                      data-testid={`admin-article-${a.stable_id}`}
                      onClick={() => setSelected(a)}
                      style={{
                        padding: '12px 14px', marginBottom: 8,
                        border: '1px solid ' + (selected?.stable_id === a.stable_id ? '#1E3A8A' : '#E2E8F0'),
                        borderRadius: 10, background: '#fff', cursor: 'pointer',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                      }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>{a.title}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#4b5a74' }}>
                        {a.slug} · rev {a.revision} · {a.status}
                      </p>
                    </div>
                    <span style={{
                      fontSize: 11, padding: '3px 8px', borderRadius: 999, fontWeight: 700,
                      background: a.status === 'published' ? '#DCFCE7' : (a.status === 'hold' ? '#FEF9C3' : '#E0E7FF'),
                      color: a.status === 'published' ? '#065F46' : (a.status === 'hold' ? '#854D0E' : '#1E3A8A'),
                    }}>{a.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <aside style={{ position: 'sticky', top: 20, alignSelf: 'start' }}>
          {!selected ? (
            <div style={{ padding: 24, border: '1px dashed #CBD5E1', borderRadius: 12, color: '#4b5a74', textAlign: 'center' }}>
              Select an article to preview, publish or roll back.
            </div>
          ) : (
            <div data-testid="admin-article-detail" style={{ padding: 20, border: '1px solid #E2E8F0', borderRadius: 12, background: '#F8FBFF' }}>
              <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A', margin: 0 }}>
                {PILLAR_LABELS[selected.pillar] || selected.pillar} · rev {selected.revision} · {selected.status}
              </p>
              <h2 style={{ fontSize: 20, margin: '10px 0', lineHeight: 1.2 }}>{selected.title}</h2>
              <p style={{ fontSize: 13, color: '#4b5a74' }}>stable_id · {selected.stable_id}</p>
              <p style={{ fontSize: 13 }}><strong>Proposed path:</strong> {selected.proposed_path}</p>
              {selected.canonical_url && (
                <p style={{ fontSize: 13 }}>
                  <strong>Canonical:</strong> <a href={selected.canonical_url} target="_blank" rel="noopener noreferrer">{selected.canonical_url} ↗</a>
                </p>
              )}
              <p style={{ fontSize: 14, color: '#263044', marginTop: 10 }}>{selected.meta_description}</p>
              <p style={{ fontSize: 13 }}>
                <Link to={`/learn/${selected.slug}`} target="_blank" rel="noopener noreferrer">Preview as /learn/{selected.slug} ↗</Link>
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <button
                  data-testid="admin-publish"
                  disabled={busy || selected.status === 'published'}
                  onClick={() => publish(selected)}
                  style={{ padding: '8px 14px', background: '#1E3A8A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: selected.status === 'published' ? 0.5 : 1 }}>
                  Publish
                </button>
                <button
                  data-testid="admin-rollback"
                  disabled={busy || selected.revision < 2}
                  onClick={() => rollback(selected)}
                  style={{ padding: '8px 14px', background: '#fff', color: '#1E3A8A', border: '1px solid #1E3A8A', borderRadius: 8, fontWeight: 700, cursor: busy ? 'wait' : 'pointer', opacity: selected.revision < 2 ? 0.4 : 1 }}>
                  Rollback
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </main>
  );
};

export default ContentEngineAdmin;
